/**
 * DESIGN PREVIEW MODE — development-only visual showcase.
 *
 * When `VITE_DESIGN_PREVIEW=1` is set in a dev environment (.env.local),
 * the app runs fully client-side with a mock user and realistic mock data,
 * so every screen can be reviewed without Supabase/TMDB/OpenAI credentials.
 *
 * This flag can never activate in production builds (`import.meta.env.DEV`
 * is compile-time false there, and the code is tree-shaken away).
 */
import type { MovieRecommendation } from './openai';

export const DESIGN_PREVIEW: boolean =
  import.meta.env.DEV && import.meta.env.VITE_DESIGN_PREVIEW === '1';

/** Visit any URL with ?signedout to review the auth screens in preview mode. */
export const PREVIEW_SIGNED_OUT: boolean =
  DESIGN_PREVIEW &&
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('signedout');

export const previewUser = {
  id: 'preview-user-0000',
  email: 'aaron@moviemeapp.com',
  user_metadata: { full_name: 'Aaron Salcido', first_name: 'Aaron' },
  app_metadata: {},
  aud: 'authenticated',
  created_at: new Date().toISOString(),
} as unknown as import('@supabase/supabase-js').User;

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

/* ------------------------------------------------------------------ */
/* Recommendations                                                     */
/* ------------------------------------------------------------------ */

export const previewRecommendations: MovieRecommendation[] = [
  {
    title: 'Inception', year: 2010, tmdbId: 27205,
    description: 'A thief who steals corporate secrets through dream-sharing technology is given the inverse task: planting an idea into the mind of a C.E.O.',
    rating: 'PG-13', genres: ['Sci-Fi', 'Thriller', 'Action'],
    cast: ['Leonardo DiCaprio', 'Joseph Gordon-Levitt', 'Elliot Page'],
    director: 'Christopher Nolan', rottenTomatoesScore: 87, imdbRating: 8.8, runtime: 148,
    posterUrl: '/posters/inception.svg',
    matchReason: 'Layered, high-concept storytelling with propulsive pacing — a strong match for your love of mind-bending thrillers.',
    matchScore: 96,
  },
  {
    title: 'Whiplash', year: 2014, tmdbId: 244786,
    description: 'A promising young drummer enrolls at a cut-throat music conservatory where his mentor will stop at nothing to realize a student\'s potential.',
    rating: 'R', genres: ['Drama', 'Music'],
    cast: ['Miles Teller', 'J.K. Simmons', 'Melissa Benoist'],
    director: 'Damien Chazelle', rottenTomatoesScore: 94, imdbRating: 8.5, runtime: 106,
    posterUrl: '/posters/whiplash.svg',
    matchReason: 'Tense, obsessive character drama with an electric finale that rewards your taste for intensity.',
    matchScore: 93,
  },
  {
    title: 'Blade Runner 2049', year: 2017, tmdbId: 335984,
    description: 'A young blade runner\'s discovery of a long-buried secret leads him to track down former blade runner Rick Deckard, who\'s been missing for thirty years.',
    rating: 'R', genres: ['Sci-Fi', 'Mystery', 'Drama'],
    cast: ['Ryan Gosling', 'Harrison Ford', 'Ana de Armas'],
    director: 'Denis Villeneuve', rottenTomatoesScore: 88, imdbRating: 8.0, runtime: 164,
    posterUrl: '/posters/blade-runner-2049.svg',
    matchReason: 'Stunning atmospheric world-building and a slow-burn mystery — cinematic sci-fi at its most visually rich.',
    matchScore: 91,
  },
  {
    title: 'The Grand Budapest Hotel', year: 2014, tmdbId: 120467,
    description: 'A legendary concierge and his trusted lobby boy become entangled in the theft of a priceless Renaissance painting amid a dramatic shift in Europe.',
    rating: 'R', genres: ['Comedy', 'Adventure'],
    cast: ['Ralph Fiennes', 'Tony Revolori', 'Saoirse Ronan'],
    director: 'Wes Anderson', rottenTomatoesScore: 92, imdbRating: 8.1, runtime: 99,
    posterUrl: '/posters/grand-budapest.svg',
    matchReason: 'Whimsical, meticulously designed comedy with heart — a palate-cleanser that still feels substantial.',
    matchScore: 88,
  },
  {
    title: 'Parasite', year: 2019, tmdbId: 496243,
    description: 'Greed and class discrimination threaten the newly formed symbiotic relationship between the wealthy Park family and the destitute Kim clan.',
    rating: 'R', genres: ['Thriller', 'Drama', 'Comedy'],
    cast: ['Song Kang-ho', 'Lee Sun-kyun', 'Cho Yeo-jeong'],
    director: 'Bong Joon-ho', rottenTomatoesScore: 99, imdbRating: 8.5, runtime: 132,
    posterUrl: '/posters/parasite.svg',
    matchReason: 'Genre-shifting brilliance: darkly funny, then gripping, then devastating. Universally acclaimed for good reason.',
    matchScore: 95,
  },
  {
    title: 'Mad Max: Fury Road', year: 2015, tmdbId: 76341,
    description: 'In a post-apocalyptic wasteland, Max teams up with Furiosa to flee from a tyrant who controls the land\'s water supply.',
    rating: 'R', genres: ['Action', 'Adventure', 'Sci-Fi'],
    cast: ['Tom Hardy', 'Charlize Theron', 'Nicholas Hoult'],
    director: 'George Miller', rottenTomatoesScore: 97, imdbRating: 8.1, runtime: 120,
    posterUrl: '/posters/fury-road.svg',
    matchReason: 'Relentless practical-effects action with real craft — pure adrenaline for a movie night.',
    matchScore: 90,
  },
  {
    title: 'Her', year: 2013, tmdbId: 152601,
    description: 'In a near future, a lonely writer develops an unlikely relationship with an operating system designed to meet his every need.',
    rating: 'R', genres: ['Romance', 'Sci-Fi', 'Drama'],
    cast: ['Joaquin Phoenix', 'Scarlett Johansson', 'Amy Adams'],
    director: 'Spike Jonze', rottenTomatoesScore: 94, imdbRating: 8.0, runtime: 126,
    posterUrl: '/posters/her.svg',
    matchReason: 'Tender, thoughtful, and quietly futuristic — an emotional counterpoint to your action picks.',
    matchScore: 86,
  },
  {
    title: 'Knives Out', year: 2019, tmdbId: 546554,
    description: 'A detective investigates the death of the patriarch of an eccentric, combative family.',
    rating: 'PG-13', genres: ['Mystery', 'Comedy', 'Crime'],
    cast: ['Daniel Craig', 'Ana de Armas', 'Chris Evans'],
    director: 'Rian Johnson', rottenTomatoesScore: 97, imdbRating: 7.9, runtime: 130,
    posterUrl: '/posters/knives-out.svg',
    matchReason: 'A modern whodunit with wit and momentum — endlessly rewatchable crowd-pleaser.',
    matchScore: 87,
  },
  {
    title: 'Arrival', year: 2016, tmdbId: 329865,
    description: 'A linguist works with the military to communicate with alien lifeforms after twelve mysterious spacecraft appear around the world.',
    rating: 'PG-13', genres: ['Sci-Fi', 'Drama'],
    cast: ['Amy Adams', 'Jeremy Renner', 'Forest Whitaker'],
    director: 'Denis Villeneuve', rottenTomatoesScore: 94, imdbRating: 7.9, runtime: 116,
    posterUrl: '/posters/arrival.svg',
    matchReason: 'Cerebral sci-fi built on language and memory, with one of the decade\'s great endings.',
    matchScore: 89,
  },
  {
    title: 'Goodfellas', year: 1990, tmdbId: 769,
    description: 'The story of Henry Hill and his life in the mob, covering his relationship with his wife and his partners in crime.',
    rating: 'R', genres: ['Crime', 'Drama'],
    cast: ['Robert De Niro', 'Ray Liotta', 'Joe Pesci'],
    director: 'Martin Scorsese', rottenTomatoesScore: 95, imdbRating: 8.7, runtime: 145,
    posterUrl: '/posters/goodfellas.svg',
    matchReason: 'The gold standard of crime cinema — kinetic, quotable, and endlessly influential.',
    matchScore: 92,
  },
  {
    title: 'Spirited Away', year: 2001, tmdbId: 129,
    description: 'During her family\'s move to the suburbs, a sullen 10-year-old girl wanders into a world ruled by gods, witches, and spirits.',
    rating: 'PG', genres: ['Animation', 'Fantasy', 'Adventure'],
    cast: ['Rumi Hiiragi', 'Miyu Irino', 'Mari Natsuki'],
    director: 'Hayao Miyazaki', rottenTomatoesScore: 96, imdbRating: 8.6, runtime: 125,
    posterUrl: '/posters/spirited-away.svg',
    matchReason: 'Imaginative, painterly animation with real emotional depth — wonder in every frame.',
    matchScore: 88,
  },
  {
    title: 'No Country for Old Men', year: 2007, tmdbId: 6977,
    description: 'Violence and mayhem ensue after a hunter stumbles upon a drug deal gone wrong and more than two million dollars in cash near the Rio Grande.',
    rating: 'R', genres: ['Thriller', 'Crime', 'Drama'],
    cast: ['Javier Bardem', 'Josh Brolin', 'Tommy Lee Jones'],
    director: 'Joel & Ethan Coen', rottenTomatoesScore: 93, imdbRating: 8.2, runtime: 122,
    posterUrl: '/posters/no-country.svg',
    matchReason: 'Merciless tension and craft from the Coens — a thriller stripped to its essentials.',
    matchScore: 90,
  },
  {
    title: 'La La Land', year: 2016, tmdbId: 313369,
    description: 'While navigating their careers in Los Angeles, a pianist and an actress fall in love while attempting to reconcile their aspirations for the future.',
    rating: 'PG-13', genres: ['Romance', 'Drama', 'Music'],
    cast: ['Ryan Gosling', 'Emma Stone', 'John Legend'],
    director: 'Damien Chazelle', rottenTomatoesScore: 91, imdbRating: 8.0, runtime: 128,
    posterUrl: '/posters/la-la-land.svg',
    matchReason: 'A love letter to movie musicals and dreamers — bittersweet in all the right ways.',
    matchScore: 85,
  },
  {
    title: 'The Thing', year: 1982, tmdbId: 1091,
    description: 'A research team in Antarctica is hunted by a shape-shifting alien that assumes the appearance of its victims.',
    rating: 'R', genres: ['Horror', 'Sci-Fi'],
    cast: ['Kurt Russell', 'Wilford Brimley', 'Keith David'],
    director: 'John Carpenter', rottenTomatoesScore: 87, imdbRating: 8.2, runtime: 109,
    posterUrl: '/posters/the-thing.svg',
    matchReason: 'Paranoia-fueled practical-effects horror that still outclasses modern imitators.',
    matchScore: 84,
  },
  {
    title: 'Before Sunrise', year: 1995, tmdbId: 76,
    description: 'A young man and woman meet on a train in Europe, and wind up spending one evening together in Vienna.',
    rating: 'R', genres: ['Romance', 'Drama'],
    cast: ['Ethan Hawke', 'Julie Delpy'],
    director: 'Richard Linklater', rottenTomatoesScore: 100, imdbRating: 8.1, runtime: 101,
    posterUrl: '/posters/before-sunrise.svg',
    matchReason: 'Two people, one night, all conversation — proof that romance needs nothing but chemistry.',
    matchScore: 83,
  },
];

export async function previewGetRecommendations(
  excludeTitles: string[] = [],
  count = 5
): Promise<MovieRecommendation[]> {
  await delay(1400); // let the loading state breathe, like a real request
  const pool = previewRecommendations.filter((m) => !excludeTitles.includes(m.title));
  return pool.slice(0, count);
}

/* ------------------------------------------------------------------ */
/* Watchlist / Watched (in-memory)                                     */
/* ------------------------------------------------------------------ */

export interface PreviewListMovie {
  id: string;
  tmdb_id: number;
  title: string;
  year: number;
  poster_url: string | null;
  overview: string;
  created_at: string;
  watched_at?: string;
}

const daysAgo = (n: number) => new Date(Date.now() - n * 864e5).toISOString();

const watchlistStore: PreviewListMovie[] = [
  { id: 'wl-1', tmdb_id: 693134, title: 'Dune: Part Two', year: 2024, poster_url: '/posters/dune-part-two.svg', overview: 'Paul Atreides unites with Chani and the Fremen while seeking revenge against the conspirators who destroyed his family.', created_at: daysAgo(2) },
  { id: 'wl-2', tmdb_id: 872585, title: 'Oppenheimer', year: 2023, poster_url: '/posters/oppenheimer.svg', overview: 'The story of American scientist J. Robert Oppenheimer and his role in the development of the atomic bomb.', created_at: daysAgo(5) },
  { id: 'wl-3', tmdb_id: 238, title: 'The Godfather', year: 1972, poster_url: '/posters/the-godfather.svg', overview: 'The aging patriarch of an organized crime dynasty transfers control of his clandestine empire to his reluctant son.', created_at: daysAgo(9) },
  { id: 'wl-4', tmdb_id: 545611, title: 'Everything Everywhere All at Once', year: 2022, poster_url: '/posters/eeaao.svg', overview: 'A middle-aged Chinese immigrant is swept up into an insane adventure in which she alone can save existence.', created_at: daysAgo(14) },
  { id: 'wl-5', tmdb_id: 949, title: 'Heat', year: 1995, poster_url: '/posters/heat.svg', overview: 'A group of high-end professional thieves start to feel the heat from the LAPD when they unknowingly leave a clue at their latest heist.', created_at: daysAgo(21) },
];

const watchedStore: PreviewListMovie[] = [
  { id: 'wd-1', tmdb_id: 155, title: 'The Dark Knight', year: 2008, poster_url: '/posters/dark-knight.svg', overview: 'Batman raises the stakes in his war on crime with the help of Lt. Jim Gordon and DA Harvey Dent.', created_at: daysAgo(3), watched_at: daysAgo(3) },
  { id: 'wd-2', tmdb_id: 157336, title: 'Interstellar', year: 2014, poster_url: '/posters/interstellar.svg', overview: 'A team of explorers travel through a wormhole in space in an attempt to ensure humanity\'s survival.', created_at: daysAgo(8), watched_at: daysAgo(8) },
  { id: 'wd-3', tmdb_id: 680, title: 'Pulp Fiction', year: 1994, poster_url: '/posters/pulp-fiction.svg', overview: 'The lives of two mob hitmen, a boxer, a gangster and his wife intertwine in four tales of violence and redemption.', created_at: daysAgo(15), watched_at: daysAgo(15) },
  { id: 'wd-4', tmdb_id: 37799, title: 'The Social Network', year: 2010, poster_url: '/posters/social-network.svg', overview: 'The story of the founding of Facebook and the lawsuits that followed.', created_at: daysAgo(22), watched_at: daysAgo(22) },
  { id: 'wd-5', tmdb_id: 348, title: 'Alien', year: 1979, poster_url: '/posters/alien.svg', overview: 'The crew of a commercial spacecraft encounter a deadly lifeform after investigating an unknown transmission.', created_at: daysAgo(30), watched_at: daysAgo(30) },
  { id: 'wd-6', tmdb_id: 289, title: 'Casablanca', year: 1942, poster_url: '/posters/casablanca.svg', overview: 'A cynical expatriate American cafe owner struggles to decide whether or not to help his former lover and her fugitive husband.', created_at: daysAgo(45), watched_at: daysAgo(45) },
];

export const previewLists = {
  async getWatchlist() { await delay(350); return [...watchlistStore]; },
  async getWatched() { await delay(350); return [...watchedStore]; },
  async addToWatchlist(m: { tmdb_id: number; title: string; year: number; poster_url: string | null; overview: string }) {
    await delay(200);
    if (!watchlistStore.some((x) => x.tmdb_id === m.tmdb_id)) {
      watchlistStore.unshift({ ...m, id: `wl-${Date.now()}`, created_at: new Date().toISOString() });
    }
  },
  async removeFromWatchlist(tmdbId: number) {
    await delay(200);
    const i = watchlistStore.findIndex((x) => x.tmdb_id === tmdbId);
    if (i >= 0) watchlistStore.splice(i, 1);
  },
  async isInWatchlist(tmdbId: number) { return watchlistStore.some((x) => x.tmdb_id === tmdbId); },
  async markAsWatched(m: { tmdb_id: number; title: string; year: number; poster_url: string | null; overview: string }) {
    await delay(200);
    if (!watchedStore.some((x) => x.tmdb_id === m.tmdb_id)) {
      const now = new Date().toISOString();
      watchedStore.unshift({ ...m, id: `wd-${Date.now()}`, created_at: now, watched_at: now });
    }
    const i = watchlistStore.findIndex((x) => x.tmdb_id === m.tmdb_id);
    if (i >= 0) watchlistStore.splice(i, 1);
  },
  async removeFromWatched(tmdbId: number) {
    await delay(200);
    const i = watchedStore.findIndex((x) => x.tmdb_id === tmdbId);
    if (i >= 0) watchedStore.splice(i, 1);
  },
  async isWatched(tmdbId: number) { return watchedStore.some((x) => x.tmdb_id === tmdbId); },
};

/* ------------------------------------------------------------------ */
/* Movie search (favorite-movie picker)                                */
/* ------------------------------------------------------------------ */

const searchPool = [
  ...previewRecommendations.map((m) => ({ id: m.tmdbId!, title: m.title, year: m.year, posterPath: m.posterUrl, overview: m.description, releaseDate: `${m.year}-01-01` })),
  ...watchlistStore.map((m) => ({ id: m.tmdb_id, title: m.title, year: m.year, posterPath: m.poster_url, overview: m.overview, releaseDate: `${m.year}-01-01` })),
  ...watchedStore.map((m) => ({ id: m.tmdb_id, title: m.title, year: m.year, posterPath: m.poster_url, overview: m.overview, releaseDate: `${m.year}-01-01` })),
];

export async function previewSearchMovies(query: string) {
  await delay(300);
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return searchPool.filter((m) => m.title.toLowerCase().includes(q)).slice(0, 8);
}

/* ------------------------------------------------------------------ */
/* Search limits                                                       */
/* ------------------------------------------------------------------ */

export const previewUsage = {
  searchCount: 7,
  lastReset: new Date(),
  remainingSearches: 23,
  limit: 30,
  periodEnd: new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth() + 1, 1)),
  isLimited: false,
};

/* ------------------------------------------------------------------ */
/* AI recommendation consent                                           */
/* ------------------------------------------------------------------ */

/** Default granted so existing rec screens stay usable in design preview. */
let previewAiConsentGranted = true;

export function previewGetAiConsent() {
  return {
    granted: previewAiConsentGranted,
    consentVersion: previewAiConsentGranted ? 'openai-recommendations-v1' : null,
    grantedAt: previewAiConsentGranted ? new Date().toISOString() : null,
    revokedAt: null as string | null,
  };
}

export function previewSetAiConsent(granted: boolean) {
  previewAiConsentGranted = granted;
  return previewGetAiConsent();
}
